import {
  AlertTriangle,
  CheckSquare,
  FileText,
  FileWarning,
  Inbox,
  LayoutList,
  ScrollText,
  Settings,
  Star,
} from "lucide-react";
import { PERMISSIONS } from "@/features/auth/permissions";

/**
 * Nav definition. `permission` is the GUID a user must hold for the item to
 * appear; items without one are visible to every signed-in user. The sidebar
 * filters on this, so a hidden route and a blocked route stay in step.
 */
export const NAV_GROUPS = [
  {
    id: "queues",
    label: "Queues",
    items: [
      {
        to: "/my-invoices",
        label: "My Invoices",
        icon: Inbox,
        permission: PERMISSIONS.MY_INVOICES,
      },
      {
        to: "/invoice-manager",
        label: "Invoices",
        icon: FileText,
        permission: PERMISSIONS.ALL_INVOICES,
      },
      { to: "/starred-invoices", label: "Starred", icon: Star },
    ],
  },
  {
    id: "exceptions",
    label: "Exceptions",
    items: [
      {
        to: "/inQuery-invoices",
        label: "In Query",
        icon: FileWarning,
        permission: PERMISSIONS.ALL_INVOICES,
      },
      {
        to: "/failed-invoices",
        label: "Failed",
        icon: AlertTriangle,
        permission: PERMISSIONS.ALL_INVOICES,
      },
    ],
  },
  {
    id: "approvals",
    label: "Approvals",
    items: [
      {
        to: "/approval-requests",
        label: "Requests",
        icon: CheckSquare,
        permission: PERMISSIONS.APPROVAL_REQUESTS,
      },
      {
        to: "/approval-rules",
        label: "Rules",
        icon: LayoutList,
        permission: PERMISSIONS.APPROVAL_RULES,
      },
    ],
  },
  {
    id: "activity",
    label: "Activity",
    items: [
      {
        to: "/logs",
        label: "Logs",
        icon: ScrollText,
        permission: PERMISSIONS.SHOW_INVOICE_CREATION_LOGS,
      },
    ],
  },
];

/** Drops items the user cannot reach, then drops groups left empty. */
export function getVisibleNavGroups(can) {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.permission || can(item.permission)),
  })).filter((group) => group.items.length > 0);
}

export const PAGE_META = {
  "/my-invoices": {
    title: "My Invoices",
    group: "Queues",
    description: "Assigned to you",
  },
  "/invoice-manager": {
    title: "Invoices",
    group: "Queues",
    description: "All inbound invoices",
  },
  "/inQuery-invoices": {
    title: "In Query",
    group: "Exceptions",
    description: "Waiting on a supplier response",
  },
  "/failed-invoices": {
    title: "Failed",
    group: "Exceptions",
    description: "Could not be processed",
  },
  "/starred-invoices": {
    title: "Starred",
    group: "Queues",
    description: "Saved for later",
  },
  "/approval-requests": {
    title: "Approval Requests",
    group: "Approvals",
    description: "Invoices awaiting your sign-off",
  },
  "/approval-rules": {
    title: "Approval Rules",
    group: "Approvals",
    description: "Conditions that route invoices for approval",
  },
  "/logs": {
    title: "Logs",
    group: "Activity",
    description: "Invoice creation history",
  },
  "/settings": {
    title: "Settings",
    group: "Workspace",
    description: "Account and preferences",
  },
  "/no-permission": {
    title: "Access denied",
    group: "Workspace",
    description: "You don't have access to this area",
  },
  "/not-found": {
    title: "Not found",
    group: "Workspace",
    description: "This page doesn't exist",
  },
};

export const SETTINGS_ITEM = { to: "/settings", label: "Settings", icon: Settings };

export function getPageTitle(pathname) {
  return PAGE_META[pathname]?.title ?? "Invoices";
}

export function getPageMeta(pathname) {
  if (pathname.startsWith("/invoice/edit/")) {
    return {
      title: "Edit invoice",
      group: "Invoices",
      description: "Review extracted fields against the supplier PDF",
    };
  }
  if (pathname.startsWith("/invoice/view/")) {
    return {
      title: "Invoice details",
      group: "Invoices",
      description: "View supplier document, comments, and activity",
    };
  }

  return (
    PAGE_META[pathname] ?? {
      title: "Invoices",
      group: "Queues",
      description: "Inbound invoices",
    }
  );
}
